import type { EditAnnotation } from "@/lib/stream";
import { normalizeViewerText } from "@/lib/viewerText";

/**
 * Center `target` within the `scrollEl` viewport. Unlike `scrollIntoView`, this
 * measures both elements with `getBoundingClientRect` (visual/zoomed space) and
 * scrolls to the resulting absolute offset, so it stays correct regardless of
 * any CSS `zoom` applied to an ancestor of `target`. Using an absolute
 * `scrollTo` (rather than a relative `scrollBy`) keeps repeated calls
 * idempotent: a second call while a smooth scroll is still animating redirects
 * to the same destination instead of compounding the delta and overshooting.
 */
export function scrollCenter(target: HTMLElement, scrollEl: HTMLElement | null) {
  if (!scrollEl) {
    return;
  }
  const targetRect = target.getBoundingClientRect();
  const viewRect = scrollEl.getBoundingClientRect();
  const delta = targetRect.top + targetRect.height / 2 - (viewRect.top + viewRect.height / 2);
  scrollEl.scrollTo({ top: scrollEl.scrollTop + delta, behavior: "smooth" });
}

/**
 * Walk the rendered DOM under `root` for the first text node containing the
 * normalized `quote` (truncated to 80 chars), wrap it in a highlight `<mark>`,
 * and center it in the scroll container.
 */
export function highlightText(root: HTMLElement, scrollEl: HTMLElement | null, quote?: string) {
  if (!quote) {
    return;
  }

  const needle = normalizeViewerText(quote.replaceAll("[[PAGE_BREAK]]", " "));
  if (!needle) {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode() as Text | null;
  while (node) {
    const haystack = normalizeViewerText(node.textContent ?? "");
    if (haystack.includes(needle.slice(0, Math.min(needle.length, 80)))) {
      const mark = document.createElement("mark");
      mark.className = "vadium-citation-highlight rounded bg-highlight px-0.5";
      node.parentElement?.insertBefore(mark, node);
      mark.appendChild(node);
      scrollCenter(mark, scrollEl);
      break;
    }
    node = walker.nextNode() as Text | null;
  }
}

/**
 * Remove all `vadium-citation-highlight` mark wrappers from `root` (replacing
 * each with its plain text node) and strip `.vadium-active-edit` from
 * tracked-change spans. Call before applying a new target so stale highlights
 * don't accumulate.
 */
export function clearTargetHighlights(root: HTMLElement) {
  root.querySelectorAll("mark.vadium-citation-highlight").forEach((mark) => {
    mark.replaceWith(document.createTextNode(mark.textContent ?? ""));
  });
  root.querySelectorAll(".vadium-active-edit").forEach((node) => node.classList.remove("vadium-active-edit"));
}

/**
 * Locate a tracked-change span by its tagged `w:id` attributes, center it in
 * the scroll container, and mark it as the active edit.
 */
export function scrollToEdit(root: HTMLElement, scrollEl: HTMLElement | null, edit?: EditAnnotation) {
  if (!edit) {
    return;
  }
  const selector = [edit.ins_w_id ? `[data-w-kind="ins"][data-w-id="${CSS.escape(edit.ins_w_id)}"]` : "", edit.del_w_id ? `[data-w-kind="del"][data-w-id="${CSS.escape(edit.del_w_id)}"]` : ""]
    .filter(Boolean)
    .join(",");
  const target = selector ? root.querySelector<HTMLElement>(selector) : null;
  if (target) {
    scrollCenter(target, scrollEl);
    target.classList.add("vadium-active-edit");
  }
}

/** Clear stale highlights, then apply the citation quote and/or edit target. */
export function applyTarget(root: HTMLElement, scrollEl: HTMLElement | null, quote?: string, edit?: EditAnnotation) {
  clearTargetHighlights(root);
  highlightText(root, scrollEl, quote);
  scrollToEdit(root, scrollEl, edit);
}
